/* Print Center (/print) — port of the prototype's pgPrint/renderSheets/doPrint.
 * Query params (doc, emp, period, scope, letter, date) come from useOverlays().openPrint(). */
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useI18n, LOOKUP } from "../../i18n";
import { useSession } from "../../app/session";
import { useOverlays } from "../../app/overlays";
import { useClock } from "../../data/hooks";
import { Alert, Button, EmptyState, ErrorBox, PageHeader, Seg, Skeleton } from "../../ui";
import { docDef, docsFor } from "./docs";
import { dims, MM, pageCss, PAPERS } from "./papers";
import { DEFAULT_STATE, fromQuery, isLetterKind, type PrState } from "./state";
import { usePrintData } from "./usePrintData";
import Controls from "./components/Controls";
import { buildPages } from "./components/buildPages";
import { renderSheets } from "./components/Sheets";
import { makeLb, type PCtx } from "./components/kit";
import "./print.css";

const TABLE_DOCS = ["register", "gosi", "wps", "directory", "attendance"];
const STYLE_ID = "print-page";


export default function PrintCenter() {
  const i = useI18n();
  const { L, t } = i;
  const { user, role } = useSession();
  const ov = useOverlays();
  const [params] = useSearchParams();
  const search = params.toString();
  const [s, setS] = useState<PrState>(() => fromQuery(params, DEFAULT_STATE));
  const [vw, setVw] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));

  /* openPrint() while already on /print → re-apply the new query */
  const lastSearch = useRef(search);
  useEffect(() => {
    if (lastSearch.current === search) return;
    lastSearch.current = search;
    setS((cur) => fromQuery(new URLSearchParams(search), cur));
  }, [search]);

  useEffect(() => {
    let tm: ReturnType<typeof setTimeout> | undefined;
    const onR = () => { clearTimeout(tm); tm = setTimeout(() => setVw(window.innerWidth), 150); };
    window.addEventListener("resize", onR);
    return () => { window.removeEventListener("resize", onR); clearTimeout(tm); };
  }, []);

  const set = useCallback((fn: (x: PrState) => PrState) => setS((x) => fn(x)), []);

  /* role rules: employees print only their own documents */
  const allowed = docsFor(role);
  const isEmp = role === "employee";
  const base: PrState = useMemo(() => {
    let x = s;
    if (!allowed.some((d) => d.key === x.doc)) x = { ...x, doc: allowed[0]?.key ?? "payslip" };
    if (isEmp) x = { ...x, emp: user?.employeeId ?? x.emp, scope: "one" };
    return x;
  }, [s, allowed, isEmp, user]);

  const pd = usePrintData(base, role, user);
  const nowHm = useClock().now.slice(11, 16);

  /* a numeric `letter` param is a letter request id → its kind + employee */
  useEffect(() => {
    if (pd.letterKind && !isLetterKind(s.letter)) setS((x) => ({ ...x, letter: pd.letterKind!, emp: pd.letterEmp ?? x.emp }));
  }, [pd.letterKind, pd.letterEmp, s.letter]);

  const eff: PrState = useMemo(() => {
    const period = base.doc === "gosi" || base.doc === "wps" ? pd.runPeriod : base.period || (isEmp ? pd.today.slice(0, 7) : pd.runPeriod);
    const emp = pd.data.emp?.id ?? base.emp;
    return { ...base, period, emp, letter: isLetterKind(base.letter) ? base.letter : pd.letterKind ?? "Salary certificate" };
  }, [base, pd.runPeriod, pd.today, pd.data.emp, pd.letterKind, isEmp]);

  const d = dims(eff);
  const doc = docDef(eff.doc);

  /* prFit: auto-grow row height when a rendered table page overflows */
  const sk = JSON.stringify(eff) + "|" + pd.data.emps.length + "|" + pd.data.slips.size + "|" + pd.data.attendance.size;
  const [fit, setFit] = useState<{ key: string; adj: number }>({ key: "", adj: 1 });
  const adj = fit.key === sk ? fit.adj : 1;

  const c: PCtx = useMemo(() => {
    const { lb, lb1, gd } = makeLb(eff.lang);
    const ar = eff.lang === "ar";
    const pick = (pair: [string, string] | undefined, k: string) => (pair ? pair[ar ? 1 : 0] : k);
    return {
      s: eff, d, tenant: pd.tenant, today: pd.today, nowHm, printedBy: user ? (ar ? user.nameAr : user.name) : "",
      lb, lb1, gd,
      dept: (k) => pick(LOOKUP.DEPT[k], k), branch: (k) => pick(LOOKUP.BR[k], k), nat: (k) => pick(LOOKUP.NAT[k], k),
      title: (e) => (ar ? LOOKUP.TITLE_AR[e.title] || e.title : e.title),
    };
  }, [eff, d.w, d.h, d.m, d.fam, pd.tenant, pd.today, nowHm, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = useMemo(() => buildPages(c, pd.data, adj, { letterTo: pd.letterTo, letterRef: pd.letterRef }), [c, pd.data, adj, pd.letterTo, pd.letterRef]);
  const preview = useMemo(() => renderSheets(c, pages, false), [c, pages]);
  const printOut = useMemo(() => renderSheets(c, pages, true), [c, pages]);

  const stageRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!TABLE_DOCS.includes(eff.doc) || !stageRef.current) return;
    let bad = false;
    stageRef.current.querySelectorAll<HTMLElement>(".sheet.fam-page .sbody").forEach((b) => { if (b.scrollHeight > b.clientHeight + 2) bad = true; });
    if (bad && adj < 1.8) setFit({ key: sk, adj: adj + 0.06 });
  });

  /* live @page rule for the chosen size (also used by Ctrl+P) */
  useEffect(() => {
    let st = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!st) { st = document.createElement("style"); st.id = STYLE_ID; document.head.appendChild(st); }
    st.textContent = pageCss(d.w, d.h || 200);
  }, [d.w, d.h]);
  useEffect(() => () => { document.getElementById(STYLE_ID)?.remove(); document.body.classList.remove("printing"); }, []);

  const doPrint = () => {
    let h = d.h;
    const host = document.getElementById("print-out");
    if (!h) {
      let mx = 0;
      stageRef.current?.querySelectorAll<HTMLElement>(".sheet").forEach((el) => { mx = Math.max(mx, el.scrollHeight); });
      h = Math.ceil(mx / MM) + 2;
      host?.querySelectorAll<HTMLElement>(".sheet").forEach((el) => { el.style.height = h + "mm"; });
    }
    let st = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!st) { st = document.createElement("style"); st.id = STYLE_ID; document.head.appendChild(st); }
    st.textContent = pageCss(d.w, h);
    document.body.classList.add("printing");
    const done = () => { document.body.classList.remove("printing"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    try { window.print(); } catch { /* print unsupported */ }
    setTimeout(done, 1500);
  };

  const avail = vw >= 1024 ? vw - (ov.collapsed ? 76 : 264) - 64 - 400 : vw - 64;
  const z = eff.zoom === "fit" ? Math.min(1, Math.max(0.2, avail / (d.w * MM))) : +eff.zoom;
  const count = preview.length;
  const pagesWord = (n: number) => L(n === 1 ? "page" : "pages", "صفحة");

  const tips: Record<string, string> = {
    page: L("In Chrome's print dialog: Margins → None, Scale → Default (100%), Background graphics → On. Choose two-sided in your printer's options.", "في نافذة الطباعة في كروم: الهوامش ← بدون، المقياس ← افتراضي، رسومات الخلفية ← تشغيل. اختر الطباعة على الوجهين من خيارات الطابعة."),
    roll: L("Install the printer's driver (Epson TM, Xprinter, Bixolon…) and set its paper to the roll width. Margins → None. The page length is set to fit the receipt.", "ثبّت تعريف الطابعة واضبط الورق على عرض اللفة. الهوامش ← بدون. يُضبط الطول تلقائيًا."),
    card: L("Card printers (Zebra, Evolis, Fargo…): select CR80, edge-to-edge, front then back. Turn on Background graphics.", "طابعات البطاقات: اختر CR80 من حافة لحافة، الوجه ثم الظهر."),
    label: L("Label printers (Zebra, Brother, Dymo, TSC…): set the label size in the driver to match, Margins → None.", "طابعات الملصقات: اضبط مقاس الملصق في التعريف، الهوامش ← بدون."),
    labelsheet: L("A4 label sheets (24 per sheet, 70 × 37 mm): Margins → None, Scale → 100%. Print the alignment test first.", "أوراق ملصقات A4: الهوامش ← بدون، المقياس 100%. اطبع صفحة اختبار أولًا."),
  };

  const header = (
    <PageHeader
      title={L("Print center", "مركز الطباعة")}
      sub={<span data-testid="print-sub">{L(doc.en, doc.ar)} · {(PAPERS[eff.paper] ?? PAPERS.A4).name} · {count} {pagesWord(count)}</span>}
      actions={<>
        <Button icon="target" onClick={() => set((x) => ({ ...x, doc: "test" }))} data-testid="btn-align-test">{L("Alignment test", "اختبار المحاذاة")}</Button>
        <Button variant="pri" icon="printer" onClick={doPrint} disabled={!count} data-testid="btn-print">{t("Print")}</Button>
      </>}
    />
  );

  if (pd.error) return <>{header}<ErrorBox error={pd.error} onRetry={pd.refetch} /></>;

  return (
    <div data-testid="print-center">
      {header}
      <div className="prgrid">
        <Controls s={eff} set={set} d={d} role={role} emps={pd.allEmps} periods={pd.periods} offboardingIds={pd.data.offboarding ? [pd.data.offboarding.employeeId] : []} />
        <section className="prv" aria-label={L("Preview", "المعاينة")}>
          <div className="prv-bar">
            <span className="sm" data-testid="print-count"><b>{count}</b> {pagesWord(count)}{eff.copies > 1 ? " · " + eff.copies + " " + L("copies", "نسخ") : ""}</span>
            <span className="spacer" />
            <Seg label={L("Zoom", "التكبير")} items={[["fit", L("Fit", "ملاءمة")], ["0.5", "50%"], ["0.75", "75%"], ["1", "100%"]]} value={eff.zoom} onChange={(v) => set((x) => ({ ...x, zoom: v }))} />
          </div>
          {pd.loading ? <Skeleton /> : count === 0 ? (
            <div className="card"><EmptyState title={L("Nothing to print", "لا يوجد ما يُطبع")} desc={emptyHint(eff.doc, L)} /></div>
          ) : (
            <div className="prv-stage" ref={stageRef} style={{ zoom: z.toFixed(3) } as React.CSSProperties} data-testid="print-preview">
              {preview.map((r) => <Fragment key={r.key}>{r.node}</Fragment>)}
            </div>
          )}
          <div className="mt"><Alert tone="info" tint title={L("Printer settings", "إعدادات الطابعة")} desc={tips[d.fam]} testId="print-tips" /></div>
        </section>
      </div>
      {typeof document !== "undefined" && createPortal(<div id="print-out" data-testid="print-out" aria-hidden="true">{printOut.map((r) => <Fragment key={r.key}>{r.node}</Fragment>)}</div>, document.body)}
    </div>
  );
}

function emptyHint(doc: string, L: (en: string, ar: string) => string): string {
  switch (doc) {
    case "voucher": return L("There are no salary advances or loans to print a voucher for.", "لا توجد سلف أو قروض لطباعة سند لها.");
    case "settlement": return L("Choose an employee to calculate the end-of-service statement.", "اختر موظفًا لحساب بيان نهاية الخدمة.");
    case "payslip": case "register": case "cheque": return L("No payslips for the chosen month and filters.", "لا توجد قسائم للشهر والتصفية المختارة.");
    default: return L("No employees match the chosen filters.", "لا يوجد موظفون مطابقون للتصفية.");
  }
}
